import { useState, useMemo, useEffect, useRef } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { rutesDB, configDB } from '../services/db'
import {
  calcularHoresEstimades,
  postposarDomicili,
  inserirRevisita,
  tempsHospital,
  optimitzarRuta,
} from '../services/routeOptimizer'
import { DIFICULTAT_CONFIG } from './DomicilisPage'
import { openNavigationApp } from '../services/taxiService'

// ── Constants d'estat ─────────────────────────────────────────────────────────

const ESTAT_ICON = {
  realitzat: '✅',
  saltat:    '❌',
  postposat: '⏭',
  pendent:   '○',
}
const ESTAT_COLOR = {
  realitzat: '#34C759',
  saltat:    '#aeaeb2',
  postposat: '#FF9500',
  pendent:   '#c7c7cc',
}

// ── Helpers de temps ──────────────────────────────────────────────────────────

function horaActualStr() {
  const ara = new Date()
  return `${String(ara.getHours()).padStart(2,'0')}:${String(ara.getMinutes()).padStart(2,'0')}`
}

function minutsActuals() {
  const ara = new Date()
  return ara.getHours() * 60 + ara.getMinutes()
}

function timeToMinuts(t) {
  if (!t) return 0
  const [h, m] = t.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

// Detecta desviació d'hora: retorna { diff } si > 15 min, null si ok
function detectarDesviacio(ruta, config) {
  const horaInici = ruta?.hora_inici ?? config?.hora_inici ?? '09:00'
  const minutsPrevist = timeToMinuts(horaInici)
  const diff = Math.abs(minutsActuals() - minutsPrevist)
  return diff > 15 ? { diff, minutsPrevist } : null
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function BadgeDificultat({ dificultat = 'normal' }) {
  const cfg = DIFICULTAT_CONFIG[dificultat] ?? DIFICULTAT_CONFIG.normal
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '2px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 500,
    }}>
      {cfg.emoji} {cfg.label}
    </span>
  )
}

function LegInfo({ leg }) {
  if (!leg) return null
  const mins = leg.mins ?? 0
  const linia = leg.linia
  return (
    <div style={S.legRow}>
      <span style={S.legLine} />
      <span style={S.legText}>
        {mins > 0 ? `${mins} min` : '—'}
        {linia ? ` · ${linia}` : (leg.tipus === 'peu' ? ' a peu' : '')}
      </span>
      <span style={S.legLine} />
    </div>
  )
}

// ── Modal: Desviació d'hora ───────────────────────────────────────────────────

function ModalDesviacio({ desviacio, horaInici, recalculant, onRecalcular, onIniciarSense, onTornar }) {
  const minutsDesv = desviacio?.diff ?? 0
  const hDesv = Math.floor(minutsDesv / 60)
  const mDesv = minutsDesv % 60
  const descDesv = hDesv > 0 ? `${hDesv}h ${mDesv}min` : `${mDesv} min`
  const ara = horaActualStr()

  return (
    <div style={S.modalOverlay}>
      <div style={S.modalBox}>
        <h3 style={S.modalTitol}>⚠️ Desviació detectada</h3>
        <p style={{ ...S.modalSub, marginBottom: 4 }}>
          La ruta estava prevista per les <strong>{horaInici}</strong>,
          però ara són les <strong>{ara}</strong>.
        </p>
        <p style={{ ...S.modalSub, marginBottom: 16, color: '#f59e0b' }}>
          Desviació: <strong>{descDesv}</strong>
        </p>

        <button
          style={{ ...S.btnPrimariBlock, opacity: recalculant ? 0.6 : 1, marginBottom: 8 }}
          onClick={onRecalcular}
          disabled={recalculant}
        >
          {recalculant ? '⏳ Recalculant…' : '🔄 Recalcular i iniciar'}
        </button>
        <button style={{ ...S.btnSecBlock, marginBottom: 8 }} onClick={onIniciarSense}>
          ▶ Iniciar sense recalcular
        </button>
        <button style={{ ...S.btnSecBlock, color: '#6b7280' }} onClick={onTornar}>
          ← Tornar
        </button>
      </div>
    </div>
  )
}

// ── Modal: Recàlcul post-fet ──────────────────────────────────────────────────

function ModalRecalcular({ recalculant, onRecalcular, onContinuar }) {
  return (
    <div style={S.modalOverlay} onClick={onContinuar}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <h3 style={S.modalTitol}>🔄 Recalcular ruta?</h3>
        <p style={S.modalSub}>
          Vols recalcular els temps d'arribada des de la teva posició actual?
        </p>
        <button
          style={{ ...S.btnPrimariBlock, opacity: recalculant ? 0.6 : 1, marginBottom: 8 }}
          onClick={onRecalcular}
          disabled={recalculant}
        >
          {recalculant ? '⏳ Recalculant…' : '🔄 Sí, recalcular ruta'}
        </button>
        <button style={S.btnSecBlock} onClick={onContinuar}>
          Continuar sense recalcular
        </button>
      </div>
    </div>
  )
}

// ── Modal: Postposar ──────────────────────────────────────────────────────────

function ModalPostposar({ domicili, ruta, config, onConfirm, onClose }) {
  const [tipusInici, setTipusInici] = useState('domicili')
  const [domiciliInici, setDomiciliInici] = useState(() => {
    const entrades = Object.entries(ruta.estat?.domicilis_estat ?? {})
    const ultim = entrades.filter(([, e]) => e.estat === 'realitzat').pop()
    return ultim?.[0] ?? ''
  })
  const [obtenintGps, setObtenintGps] = useState(false)
  const [gpsError, setGpsError]       = useState(null)

  const domicilisSeq = (ruta.sequencia ?? []).filter(p => p.tipus === 'domicili')

  async function confirmar() {
    if (tipusInici === 'gps') {
      if (!navigator.geolocation) { setGpsError('El dispositiu no suporta GPS'); return }
      setObtenintGps(true); setGpsError(null)
      try {
        const coords = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(
            pos => res({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            rej, { timeout: 10000 }
          )
        )
        await onConfirm(tipusInici, null, coords)
      } catch {
        setGpsError('No s\'ha pogut obtenir la posició GPS')
        setObtenintGps(false)
      }
    } else {
      await onConfirm(tipusInici, domiciliInici, null)
    }
  }

  const opcions = [
    { id: 'gps',      icon: '📍', label: 'Posició actual',  sub: 'Usar GPS del dispositiu' },
    { id: 'domicili', icon: '🏠', label: 'Domicili',        sub: null },
    { id: 'hospital', icon: '🏥', label: 'Hospital',        sub: config?.hospital?.nom || 'Hospital de referència' },
    { id: 'casa',     icon: '🏡', label: 'Casa',            sub: config?.ubicacio_inici?.adreca?.slice(0, 22) || 'Adreça de casa' },
  ]

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <button style={S.modalClose} onClick={onClose}>✕</button>
        <h3 style={S.modalTitol}>⏭ Postposar {domicili.codi}</h3>
        <p style={S.modalSub}>On ets ara? Triarem la millor ruta des d'aquí.</p>
        <div style={S.opcionesGrid}>
          {opcions.map(op => (
            <div key={op.id} style={{
              ...S.opcioCard,
              borderColor:     tipusInici === op.id ? '#007AFF' : '#e5e7eb',
              backgroundColor: tipusInici === op.id ? '#F0F7FF' : '#fff',
            }} onClick={() => setTipusInici(op.id)}>
              <div style={S.opcioIcon}>{op.icon}</div>
              <div style={S.opcioLabel}>{op.label}</div>
              {op.id === 'domicili' && tipusInici === 'domicili' ? (
                <select value={domiciliInici} onChange={e => setDomiciliInici(e.target.value)}
                  onClick={e => e.stopPropagation()} style={S.opcioSelect}>
                  <option value="">— Tria domicili —</option>
                  {domicilisSeq.map(p => {
                    const clau  = p.codi_visita || p.codi
                    const estat = ruta.estat?.domicilis_estat?.[clau]?.estat
                    return (
                      <option key={clau} value={clau}>
                        {estat === 'realitzat' ? '✅ ' : ''}{p.codi}
                        {p.adreca ? ` – ${p.adreca.slice(0, 18)}` : ''}
                      </option>
                    )
                  })}
                </select>
              ) : op.sub ? (
                <div style={S.opcioSub}>{op.sub}</div>
              ) : null}
            </div>
          ))}
        </div>
        {gpsError && <p style={{ color: '#ef4444', fontSize: 12, margin: '6px 0 0' }}>{gpsError}</p>}
        <button style={{ ...S.btnPrimariBlock, opacity: obtenintGps ? 0.6 : 1 }}
          onClick={confirmar} disabled={obtenintGps}>
          {obtenintGps ? '⏳ Obtenint GPS…' : '⏭ Postposar i recalcular ruta'}
        </button>
        <button style={S.btnSecBlock} onClick={onClose}>Cancel·lar</button>
      </div>
    </div>
  )
}

// ── Modal: Revisitar ──────────────────────────────────────────────────────────

function ModalRevisitar({ domicili, onConfirm, onClose }) {
  const [minuts, setMinuts] = useState(60)
  const dreceres = [30, 60, 90, 120, 180]

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <button style={S.modalClose} onClick={onClose}>✕</button>
        <h3 style={S.modalTitol}>🔄 Revisitar {domicili.codi}</h3>
        {domicili.adreca && <p style={S.modalSub}>{domicili.adreca}</p>}
        <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 8 }}>
          Temps fins la propera visita:
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <input type="number" value={minuts}
            onChange={e => setMinuts(Math.max(15, parseInt(e.target.value) || 15))}
            min={15} max={480} step={15}
            style={{ width: 72, fontSize: 22, textAlign: 'center', padding: '8px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <span style={{ fontSize: 16, color: '#374151' }}>minuts</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {dreceres.map(m => (
            <button key={m} onClick={() => setMinuts(m)} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border:          `1px solid ${minuts === m ? '#007AFF' : '#e5e7eb'}`,
              backgroundColor: minuts === m ? '#007AFF' : '#fff',
              color:           minuts === m ? '#fff' : '#374151',
            }}>
              {m >= 60 ? `${m / 60}h` : `${m}min`}
            </button>
          ))}
        </div>
        <button style={S.btnPrimariBlock} onClick={() => onConfirm(minuts)}>🔄 Afegir revisita</button>
        <button style={S.btnSecBlock} onClick={onClose}>Cancel·lar</button>
      </div>
    </div>
  )
}

// ── Resum final ───────────────────────────────────────────────────────────────

function ResumFinalRuta({ sequencia, estat, ruta }) {
  if (!estat?.finalitzat) return null

  const domicilis = sequencia.filter(p => p.tipus === 'domicili' || p.tipus === 'hospital')
  const codisReals = new Set(sequencia.filter(p => p.tipus === 'domicili').map(p => p.codi))
  const estatEntries = Object.entries(estat.domicilis_estat ?? {})
  const realitzats = [...codisReals].filter(codi =>
    estatEntries.some(([clau, e]) => clau.startsWith(codi) && e.estat === 'realitzat')
  ).length
  const saltats = [...codisReals].filter(codi =>
    estatEntries.every(([clau, e]) => !clau.startsWith(codi) || e.estat === 'saltat') &&
    estatEntries.some(([clau]) => clau.startsWith(codi))
  ).length
  const postposats = Object.values(estat.domicilis_estat ?? {}).filter(e => e.estat === 'postposat').length

  // Calcula durada real
  let duradaReal = null
  if (estat.hora_inici_real && estat.hora_fi_real) {
    duradaReal = timeToMinuts(estat.hora_fi_real) - timeToMinuts(estat.hora_inici_real)
    if (duradaReal < 0) duradaReal += 24 * 60
  }

  // Desviació mitjana per als nodes amb hora real i estimada
  const desviacions = domicilis.map(p => {
    const clau = p.codi_visita || p.codi
    const e    = estat.domicilis_estat?.[clau]
    if (!e?.hora_arribada_real || !e?.hora_arribada_estimada) return null
    return timeToMinuts(e.hora_arribada_real) - timeToMinuts(e.hora_arribada_estimada)
  }).filter(d => d !== null)
  const desviMitjana = desviacions.length
    ? Math.round(desviacions.reduce((a, b) => a + b, 0) / desviacions.length)
    : null

  // Historial de modificacions
  const mods = estat.modificacions ?? []

  return (
    <div style={S.resumBloc}>
      {/* Estadístiques generals */}
      <div style={S.resumStats}>
        <div style={S.resumStat}>
          <span style={{ ...S.resumStatVal, color: '#34C759' }}>{realitzats}</span>
          <span style={S.resumStatLabel}>realitzats</span>
        </div>
        <div style={S.resumStat}>
          <span style={{ ...S.resumStatVal, color: '#aeaeb2' }}>{saltats}</span>
          <span style={S.resumStatLabel}>saltats</span>
        </div>
        {postposats > 0 && (
          <div style={S.resumStat}>
            <span style={{ ...S.resumStatVal, color: '#FF9500' }}>{postposats}</span>
            <span style={S.resumStatLabel}>postposats</span>
          </div>
        )}
        {duradaReal !== null && (
          <div style={S.resumStat}>
            <span style={S.resumStatVal}>{duradaReal}′</span>
            <span style={S.resumStatLabel}>durada real</span>
          </div>
        )}
      </div>

      {/* Previst vs real */}
      <p style={S.resumSeccioTitol}>Previst vs real</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={S.resumTemps}>
          <span style={S.resumTempsLabel}>Inici previst</span>
          <span style={S.resumTempsVal}>{ruta.hora_inici ?? '—'}</span>
        </div>
        <div style={S.resumTemps}>
          <span style={S.resumTempsLabel}>Inici real</span>
          <span style={S.resumTempsVal}>{estat.hora_inici_real ?? '—'}</span>
        </div>
        {ruta.hora_fi_prevista && (
          <div style={S.resumTemps}>
            <span style={S.resumTempsLabel}>Fi prevista</span>
            <span style={S.resumTempsVal}>{ruta.hora_fi_prevista}</span>
          </div>
        )}
        <div style={S.resumTemps}>
          <span style={S.resumTempsLabel}>Fi real</span>
          <span style={S.resumTempsVal}>{estat.hora_fi_real ?? '—'}</span>
        </div>
        {desviMitjana !== null && (
          <div style={{ ...S.resumTemps, borderColor: desviMitjana > 10 ? '#FED7AA' : '#bbf7d0', backgroundColor: desviMitjana > 10 ? '#FFF8F0' : '#f0fdf4' }}>
            <span style={S.resumTempsLabel}>Desv. mitjana</span>
            <span style={{ ...S.resumTempsVal, color: desviMitjana > 10 ? '#FF9500' : '#16a34a' }}>
              {desviMitjana > 0 ? '+' : ''}{desviMitjana} min
            </span>
          </div>
        )}
      </div>

      {/* Detall per domicili */}
      {domicilis.length > 0 && (
        <>
          <p style={S.resumSeccioTitol}>Detall per visita</p>
          {domicilis.map((p, i) => {
            const clau = p.codi_visita || p.codi
            const e    = estat.domicilis_estat?.[clau]
            if (!e) return null
            const desv = (e.hora_arribada_real && e.hora_arribada_estimada)
              ? timeToMinuts(e.hora_arribada_real) - timeToMinuts(e.hora_arribada_estimada)
              : null
            return (
              <div key={`${clau}-${i}`} style={S.resumDetallFila}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', flex: 1 }}>
                  {ESTAT_ICON[e.estat] ?? '○'} {p.codi}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {e.hora_arribada_estimada ?? '—'} → {e.hora_arribada_real ?? '—'}
                  {desv !== null && (
                    <span style={{ color: desv > 10 ? '#FF9500' : desv < -5 ? '#34C759' : '#9ca3af', marginLeft: 4 }}>
                      ({desv > 0 ? '+' : ''}{desv} min)
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </>
      )}

      {/* Historial de modificacions */}
      {mods.length > 0 && (
        <>
          <p style={S.resumSeccioTitol}>Modificacions ({mods.length})</p>
          {mods.map((mod, i) => (
            <div key={i} style={{ fontSize: 11, color: '#9ca3af', padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}>
              {mod.tipus === 'revisita'  && `🔄 Revisita de ${mod.codi}`}
              {mod.tipus === 'postposat' && `⏭ ${mod.codi} postposat`}
              {mod.tipus === 'recalcul'  && `📍 Recàlcul des de posició actual`}
              <span style={{ marginLeft: 6 }}>
                {new Date(mod.timestamp).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

// ── Component principal ───────────────────────────────────────────────────────

export default function RouteExecutionView({ rutaId, onBack }) {
  const ruta   = useLiveQuery(() => rutaId ? rutesDB.getById(rutaId) : null, [rutaId])
  const config = useLiveQuery(() => configDB.getOrDefault(), [])

  const [modal, setModal]             = useState(null) // { tipus, domicili }
  const [guardant, setGuardant]       = useState(false)
  const [recalculant, setRecalculant] = useState(false)
  const [avisDesviacio, setAvisDesviacio] = useState(null)  // desviació pre-inici
  const [mostrarVisitats, setMostrarVisitats] = useState(true)
  const iniciantRef = useRef(null)

  // Parseja sequencia i legs des de la BD
  const sequencia = useMemo(() => {
    if (!ruta?.sequencia_json) return []
    try { return JSON.parse(ruta.sequencia_json) } catch { return [] }
  }, [ruta?.sequencia_json])

  const legs = useMemo(() => {
    if (!ruta?.legs_json) return []
    try { return JSON.parse(ruta.legs_json) } catch { return [] }
  }, [ruta?.legs_json])

  const estat = useMemo(() => {
    if (!ruta?.estat_json) return null
    try { return JSON.parse(ruta.estat_json) } catch { return null }
  }, [ruta?.estat_json])

  // Restriccions de la ruta (objecte per codi, ex: { HADO1: { hospital: 'avant', temps_hospital: 15 } })
  const restriccions = useMemo(() => {
    if (!ruta?.restriccions) return null
    try { return JSON.parse(ruta.restriccions) } catch { return null }
  }, [ruta?.restriccions])

  const horesEstimades = useMemo(() => {
    if (!sequencia.length) return null
    return calcularHoresEstimades(sequencia, legs, ruta?.hora_inici ?? '09:00', config, restriccions)
  }, [sequencia, legs, ruta?.hora_inici, config, restriccions])

  // Auto-inicialitza: comprova desviació primer
  useEffect(() => {
    if (!rutaId || !ruta?.id)    return
    if (estat?.iniciat)          return
    if (!sequencia.length)       return
    if (iniciantRef.current === rutaId) return
    iniciantRef.current = rutaId

    const desv = detectarDesviacio(ruta, config)
    if (desv) {
      setAvisDesviacio(desv)
    } else {
      iniciarRuta()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta?.id, sequencia.length, estat?.iniciat])

  async function saveEstat(nouEstat) {
    await rutesDB.update(rutaId, { estat_json: JSON.stringify(nouEstat) })
  }

  // Construeix domicilis_estat initial (inclou hospitals)
  function buildDomicilisEstat(seqArr, hores) {
    const domicilisEstat = {}
    seqArr.forEach((punt, i) => {
      if (punt.tipus !== 'domicili' && punt.tipus !== 'hospital') return
      const clauEstat = punt.codi_visita || punt.codi
      const clauETA   = punt.codi_visita || `${punt.codi}_${i}`
      domicilisEstat[clauEstat] = {
        estat:                  'pendent',
        hora_arribada_estimada: hores?.[clauETA]?.hora_arribada ?? null,
        hora_sortida_estimada:  hores?.[clauETA]?.hora_sortida  ?? null,
        hora_arribada_real:     null,
        hora_sortida_real:      null,
        notes:                  '',
      }
    })
    return domicilisEstat
  }

  async function iniciarRuta(novaHoraInici) {
    const horaInici = novaHoraInici ?? ruta?.hora_inici ?? '09:00'
    const hores = calcularHoresEstimades(sequencia, legs, horaInici, config, restriccions)
    const domicilisEstat = buildDomicilisEstat(sequencia, hores)
    await saveEstat({
      iniciat:         true,
      finalitzat:      false,
      hora_inici_real: horaActualStr(),
      posicio_actual:  null,
      domicilis_estat: domicilisEstat,
      modificacions:   [],
    })
    setAvisDesviacio(null)
  }

  // Recalcula la ruta amb l'hora actual i reinicia ETAs
  async function handleRecalcularIIniciar() {
    setRecalculant(true)
    try {
      const horaAra = horaActualStr()
      // Recalcula hores estimades amb hora actual
      const novesHores = calcularHoresEstimades(sequencia, legs, horaAra, config, restriccions)
      const domicilisEstat = buildDomicilisEstat(sequencia, novesHores)
      await rutesDB.update(rutaId, {
        hora_inici: horaAra,
        estat_json: JSON.stringify({
          iniciat:         true,
          finalitzat:      false,
          hora_inici_real: horaAra,
          posicio_actual:  null,
          domicilis_estat: domicilisEstat,
          modificacions:   [{ timestamp: new Date().toISOString(), tipus: 'recalcul_inici', hora_original: ruta?.hora_inici }],
        }),
      })
      setAvisDesviacio(null)
    } catch (e) {
      console.error('[RecalcularIIniciar]', e)
    } finally {
      setRecalculant(false)
    }
  }

  async function marcarEstat(codiVisita, nouEstat) {
    if (!estat) return
    const ara = horaActualStr()
    const nouEstatComplet = {
      ...estat,
      domicilis_estat: {
        ...estat.domicilis_estat,
        [codiVisita]: {
          ...estat.domicilis_estat?.[codiVisita],
          estat:              nouEstat,
          hora_arribada_real: nouEstat === 'realitzat' && !estat.domicilis_estat?.[codiVisita]?.hora_arribada_real
            ? ara
            : estat.domicilis_estat?.[codiVisita]?.hora_arribada_real ?? null,
          hora_sortida_real:  nouEstat === 'realitzat' ? ara : estat.domicilis_estat?.[codiVisita]?.hora_sortida_real ?? null,
        },
      },
    }
    await saveEstat(nouEstatComplet)
    // Mostra modal de recàlcul només en marcar com a realitzat
    if (nouEstat === 'realitzat') {
      setModal({ tipus: 'recalcular', clauUltimFet: codiVisita })
    }
  }

  // Recàlcul de ruta des de posició actual (GPS o haversine)
  async function handleRecalcularDesPosicio() {
    setRecalculant(true)
    try {
      const pendents = sequencia.filter(p => {
        if (p.tipus !== 'domicili') return false
        const clau = p.codi_visita || p.codi
        const e    = estat?.domicilis_estat?.[clau]?.estat
        return e !== 'realitzat' && e !== 'saltat'
      })
      if (!pendents.length) { setModal(null); return }

      // Recalcula ETAs per als pendents amb hora actual
      const horaAra = horaActualStr()
      // Construeix nova seqüència: inici + hospitals + pendents
      const novaSeq = sequencia.filter(p =>
        p.tipus === 'inici' ||
        p.tipus === 'hospital' ||
        pendents.some(pd => (pd.codi_visita || pd.codi) === (p.codi_visita || p.codi))
      )
      const novesHores = calcularHoresEstimades(novaSeq, legs, horaAra, config, restriccions)

      // Actualitza ETAs als pendents dins domicilis_estat
      const nousDomicilisEstat = { ...estat.domicilis_estat }
      pendents.forEach((p, i) => {
        const clauEstat = p.codi_visita || p.codi
        const clauETA   = p.codi_visita || `${p.codi}_${novaSeq.indexOf(p)}`
        if (nousDomicilisEstat[clauEstat]) {
          nousDomicilisEstat[clauEstat] = {
            ...nousDomicilisEstat[clauEstat],
            hora_arribada_estimada: novesHores?.[clauETA]?.hora_arribada ?? nousDomicilisEstat[clauEstat].hora_arribada_estimada,
            hora_sortida_estimada:  novesHores?.[clauETA]?.hora_sortida  ?? nousDomicilisEstat[clauEstat].hora_sortida_estimada,
          }
        }
      })

      await saveEstat({
        ...estat,
        domicilis_estat: nousDomicilisEstat,
        modificacions: [
          ...(estat.modificacions ?? []),
          { timestamp: new Date().toISOString(), tipus: 'recalcul', hora_recalcul: horaAra },
        ],
      })
    } catch (e) {
      console.error('[RecalcularDesPosicio]', e)
    } finally {
      setRecalculant(false)
      setModal(null)
    }
  }

  async function handlePostposar(tipusInici, domiciliIniciCodi, gpsCoords) {
    if (!modal?.domicili || !ruta) return
    setGuardant(true)
    try {
      const codiVisita   = modal.domicili.codi_visita || modal.domicili.codi
      const rutaAmbEstat = { ...ruta, sequencia, legs, estat }
      const resultat     = await postposarDomicili(
        rutaAmbEstat, codiVisita, tipusInici, domiciliIniciCodi, config, gpsCoords
      )
      await rutesDB.update(rutaId, {
        sequencia_json: JSON.stringify(resultat.sequencia),
        legs_json:      JSON.stringify(resultat.legs),
        estat_json:     JSON.stringify(resultat.estat),
      })
      setModal(null)
    } catch (e) {
      console.error('[Postposar]', e)
    } finally {
      setGuardant(false)
    }
  }

  async function handleRevisitar(domicili, minuts) {
    if (!estat) return
    setGuardant(true)
    try {
      const codiVisita  = domicili.codi_visita || domicili.codi
      const indexActual = sequencia.findIndex(p => (p.codi_visita || p.codi) === codiVisita)
      const nouCodi     = `${domicili.codi}_rev${Date.now()}`

      const novaVisita = {
        ...domicili,
        codi_visita:  nouCodi,
        visita_num:   null,
        visita_total: null,
        tipus:        'domicili',
      }

      const indexInsercio = Math.min(indexActual + 2, sequencia.length)
      const { novaSequencia, novaLegs } = inserirRevisita(
        sequencia, legs, indexInsercio, novaVisita
      )

      const novesHores = calcularHoresEstimades(
        novaSequencia, novaLegs, ruta?.hora_inici ?? '09:00', config, restriccions
      )

      const nouEstat = {
        ...estat,
        domicilis_estat: {
          ...estat.domicilis_estat,
          [nouCodi]: {
            estat:                  'pendent',
            hora_arribada_estimada: novesHores?.[nouCodi]?.hora_arribada ?? null,
            hora_sortida_estimada:  novesHores?.[nouCodi]?.hora_sortida  ?? null,
            hora_arribada_real:     null,
            hora_sortida_real:      null,
            notes:                  `Revisita de ${domicili.codi} (+${minuts} min)`,
          },
        },
        modificacions: [
          ...(estat.modificacions ?? []),
          {
            timestamp:        new Date().toISOString(),
            tipus:            'revisita',
            codi:             codiVisita,
            codi_nova_visita: nouCodi,
            interval_minuts:  minuts,
          },
        ],
      }

      await rutesDB.update(rutaId, {
        sequencia_json: JSON.stringify(novaSequencia),
        legs_json:      JSON.stringify(novaLegs),
        estat_json:     JSON.stringify(nouEstat),
      })
      setModal(null)
    } catch (e) {
      console.error('[Revisitar]', e)
    } finally {
      setGuardant(false)
    }
  }

  async function finalitzarRuta() {
    if (!estat) return
    await saveEstat({ ...estat, finalitzat: true, hora_fi_real: horaActualStr() })
  }

  // ── Càlculs derivats ────────────────────────────────────────────────────────

  const trackableTypes = ['domicili', 'hospital']

  const domiciliActual = sequencia.find(p => {
    if (!trackableTypes.includes(p.tipus)) return false
    const clau = p.codi_visita || p.codi
    return (estat?.domicilis_estat?.[clau]?.estat ?? 'pendent') === 'pendent'
  })

  const nTotal      = sequencia.filter(p => trackableTypes.includes(p.tipus)).length
  const nRealitzats = Object.values(estat?.domicilis_estat ?? {}).filter(e => e.estat === 'realitzat').length
  const nSaltats    = Object.values(estat?.domicilis_estat ?? {}).filter(e => e.estat === 'saltat').length

  // Filtre per mostrarVisitats
  const sequenciaVisible = useMemo(() => {
    if (mostrarVisitats) return sequencia
    return sequencia.filter(p => {
      if (!trackableTypes.includes(p.tipus)) return true // inici sempre visible
      const clau  = p.codi_visita || p.codi
      const estat_ = estat?.domicilis_estat?.[clau]?.estat ?? 'pendent'
      return estat_ !== 'realitzat' && estat_ !== 'saltat'
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequencia, estat, mostrarVisitats])

  // ── Render: loading ─────────────────────────────────────────────────────────

  if (!ruta) {
    return (
      <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#9ca3af' }}>Carregant ruta…</p>
      </div>
    )
  }

  // ── Render: modal desviació (pre-inici) ─────────────────────────────────────

  if (avisDesviacio && !estat?.iniciat) {
    return (
      <div style={S.page}>
        <header style={S.header}>
          <button style={S.btnBack} onClick={onBack}>←</button>
          <h1 style={S.headerTitol}>{ruta.nom}</h1>
        </header>
        <ModalDesviacio
          desviacio={avisDesviacio}
          horaInici={ruta.hora_inici ?? '09:00'}
          recalculant={recalculant}
          onRecalcular={handleRecalcularIIniciar}
          onIniciarSense={() => { setAvisDesviacio(null); iniciarRuta() }}
          onTornar={onBack}
        />
      </div>
    )
  }

  // ── Render: iniciant (transitori) ───────────────────────────────────────────

  if (!estat?.iniciat) {
    return (
      <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <p style={{ color: '#9ca3af', fontSize: 15 }}>Iniciant ruta…</p>
      </div>
    )
  }

  // ── Render: finalitzada ─────────────────────────────────────────────────────

  if (estat?.finalitzat) {
    return (
      <div style={S.page}>
        <header style={S.header}>
          <button style={S.btnBack} onClick={onBack}>←</button>
          <h1 style={S.headerTitol}>{ruta.nom}</h1>
        </header>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
            <span style={{ fontSize: 56 }}>🏁</span>
            <h2 style={{ margin: '8px 0 4px', fontSize: 22, fontWeight: 700, color: '#111827' }}>Ruta completada!</h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {nRealitzats} realitzats · {nSaltats} saltats
            </p>
            <p style={{ color: '#9ca3af', fontSize: 13, margin: '4px 0 0' }}>
              Inici: {estat.hora_inici_real} · Fi: {estat.hora_fi_real}
            </p>
          </div>
          <ResumFinalRuta sequencia={sequencia} estat={estat} ruta={ruta} />
          <button style={{ ...S.btnPrimariBlock, marginTop: 16 }} onClick={onBack}>← Tornar a rutes</button>
        </div>
      </div>
    )
  }

  // ── Render: en execució ─────────────────────────────────────────────────────

  const tempsHosp = tempsHospital(config)
  const ultimIdx     = sequencia.length - 1
  const ultimClauFi  = sequencia[ultimIdx]?.codi_visita || `${sequencia[ultimIdx]?.codi}_${ultimIdx}`
  const horaFiActual  = horesEstimades?.[ultimClauFi]?.hora_sortida ?? null
  const horaFiPrevista = ruta.hora_fi_prevista ?? null
  const desviacio = horaFiActual && horaFiPrevista
    ? timeToMinuts(horaFiActual) - timeToMinuts(horaFiPrevista)
    : 0

  return (
    <div style={S.page}>
      {/* Capçalera */}
      <header style={S.header}>
        <button style={S.btnBack} onClick={onBack}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={S.headerTitol}>{ruta.nom}</h1>
          <p style={S.headerSub}>
            ● En curs · {nRealitzats}/{nTotal} visites
            {estat.hora_inici_real && ` · Iniciada ${estat.hora_inici_real}`}
          </p>
        </div>
        {/* Switch mostrar visitats */}
        <div style={S.switchWrap} onClick={() => setMostrarVisitats(v => !v)}>
          <span style={{ fontSize: 10, color: '#9ca3af' }}>{mostrarVisitats ? 'Tots' : 'Pendents'}</span>
          <div style={{ ...S.switchTrack, background: mostrarVisitats ? '#34C759' : '#ddd' }}>
            <div style={{ ...S.switchThumb, left: mostrarVisitats ? 15 : 3 }} />
          </div>
        </div>
        {!domiciliActual && (
          <button style={S.btnFinalitzar} onClick={finalitzarRuta}>🏁 Fi</button>
        )}
      </header>

      {/* Seqüència */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px 120px' }}>
        {sequenciaVisible.map((punt, vi) => {
          // índex real a sequencia per ETA keys
          const i         = sequencia.indexOf(punt)
          const clau      = punt.codi_visita || `${punt.codi}_${i}`
          const clauEstat = punt.codi_visita || punt.codi
          const estatDom  = estat.domicilis_estat?.[clauEstat]
          const estatVal  = estatDom?.estat ?? 'pendent'
          const esActual  = domiciliActual && (clauEstat === (domiciliActual.codi_visita || domiciliActual.codi))
          const esDone    = estatVal === 'realitzat' || estatVal === 'saltat'

          return (
            <div key={`${clau}-${vi}`}>
              {vi > 0 && !esDone && <LegInfo leg={legs?.[i - 1]} />}

              {punt.tipus === 'inici' && (
                <div style={{ ...S.puntCard, borderLeftColor: '#007AFF', opacity: 0.7 }}>
                  <div style={S.puntTop}>
                    <span style={S.puntIcona}>📍</span>
                    <span style={{ ...S.puntCodi, color: '#007AFF' }}>Inici</span>
                    {horesEstimades?.[clau]?.hora_arribada && (
                      <span style={S.puntHora}>{horesEstimades[clau].hora_arribada}</span>
                    )}
                  </div>
                  {punt.adreca && <p style={S.puntAdreca}>{punt.adreca}</p>}
                </div>
              )}

              {punt.tipus === 'hospital' && (() => {
                const esHospActual = domiciliActual && clauEstat === (domiciliActual.codi_visita || domiciliActual.codi)
                return (
                  <div style={{
                    ...S.puntCard,
                    borderLeftColor: esHospActual ? '#FF9500' : esDone ? '#aeaeb2' : '#FF9500',
                    backgroundColor: esHospActual ? '#FFF8F0' : esDone ? '#fafafa' : '#FFF8F0',
                    opacity: estatVal === 'saltat' ? 0.55 : 1,
                  }}>
                    <div style={S.puntTop}>
                      <span style={S.puntIcona}>{esDone ? ESTAT_ICON[estatVal] : '🏥'}</span>
                      <span style={{ ...S.puntCodi, color: esDone ? '#9ca3af' : '#FF9500' }}>Hospital</span>
                      <span style={{ ...S.puntHora, color: esDone ? '#9ca3af' : '#374151' }}>
                        {estatDom?.hora_arribada_real
                          ? `↳ ${estatDom.hora_arribada_real}`
                          : estatDom?.hora_arribada_estimada ?? horesEstimades?.[clau]?.hora_arribada ?? ''}
                      </span>
                    </div>
                    {punt.adreca && <p style={S.puntAdreca}>{punt.adreca}</p>}
                    {!esDone && (() => {
                      const lat = punt.lat || punt.dades_json?.lat
                      const lng = punt.lng || punt.dades_json?.lng
                      const adreca = punt.adreca || punt.nom
                      return lat && lng ? (
                        <button
                          onClick={() => openNavigationApp(lat, lng, adreca, 'auto')}
                          style={S.btnGuiar}
                        >
                          🧭 Guiar fins aquí
                        </button>
                      ) : null
                    })()}
                    {esHospActual && (
                      <>
                        <p style={{ fontSize: 11, color: '#FF9500', margin: '4px 0 0', fontWeight: 600 }}>
                          🏥 Temps total: {horesEstimades?.[clau]?.temps_estada ?? tempsHosp} min
                        </p>
                        {horesEstimades?.[clau]?.domicilis_vinculats?.length > 0 && (
                          <div style={{ fontSize: 11, color: '#9ca3af', margin: '3px 0 0 2px' }}>
                            Inclou:
                            {horesEstimades[clau].domicilis_vinculats.map(codi => (
                              <div key={codi} style={{ paddingLeft: 10, paddingTop: 2 }}>
                                • {codi}: {restriccions?.[codi]?.temps_hospital_abans ?? restriccions?.[codi]?.temps_hospital ?? config?.temps_atencio?.normal ?? 30} min
                                {(restriccions?.[codi]?.hospital_abans === 'just_abans' || restriccions?.[codi]?.hospital === 'just_abans') && (
                                  <span style={{ color: '#FF9500' }}> (just abans)</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={S.accionsDomicili}>
                          <button
                            style={{ ...S.btnAccio, backgroundColor: '#34C759', color: '#fff' }}
                            onClick={() => marcarEstat(clauEstat, 'realitzat')}
                            disabled={guardant}
                          >
                            ✅ Fet
                          </button>
                          <button
                            style={{ ...S.btnAccio, backgroundColor: '#aeaeb2', color: '#fff' }}
                            onClick={() => marcarEstat(clauEstat, 'saltat')}
                            disabled={guardant}
                          >
                            ⏭ Saltar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              })()}

              {punt.tipus === 'domicili' && (
                <div style={{
                  ...S.puntCard,
                  borderLeftColor: esActual ? '#007AFF' : ESTAT_COLOR[estatVal],
                  backgroundColor: esActual ? '#F0F7FF' : esDone ? '#fafafa' : '#fff',
                  opacity: estatVal === 'saltat' ? 0.55 : 1,
                }}>
                  <div style={S.puntTop}>
                    <span style={{ ...S.puntIcona, color: esActual ? '#007AFF' : ESTAT_COLOR[estatVal] }}>
                      {esActual ? '▶' : ESTAT_ICON[estatVal]}
                    </span>
                    <span style={{ ...S.puntCodi, color: esActual ? '#007AFF' : esDone ? '#9ca3af' : '#111827' }}>
                      {punt.codi}
                    </span>
                    {punt.codi_visita && (punt.visita_total ?? 1) > 1 && (
                      <span style={S.visitaBadge}>V{punt.visita_num}</span>
                    )}
                    <span style={{ ...S.puntHora, color: esDone ? '#9ca3af' : '#374151' }}>
                      {estatDom?.hora_arribada_real
                        ? `↳ ${estatDom.hora_arribada_real}`
                        : estatDom?.hora_arribada_estimada ?? horesEstimades?.[clau]?.hora_arribada ?? ''}
                    </span>
                  </div>

                  {punt.adreca && (
                    <p style={{ ...S.puntAdreca, color: esDone ? '#9ca3af' : '#374151' }}>{punt.adreca}</p>
                  )}
                  {punt.dades_json?.persona_nom && (
                    <p style={{ ...S.puntAdreca, color: esDone ? '#c4c4c4' : '#6b7280' }}>
                      👤 {punt.dades_json.persona_nom}
                    </p>
                  )}

                  {!esDone && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {punt.dades_json?.dificultat && punt.dades_json.dificultat !== 'normal' && (
                        <BadgeDificultat dificultat={punt.dades_json.dificultat} />
                      )}
                      {punt.dades_json?.estat === 'ingres' && (
                        <span style={S.badgeIngres}>🟢 INGRÉS</span>
                      )}
                      {punt.dades_json?.estat === 'alta' && (
                        <span style={S.badgeAlta}>🔴 ALTA</span>
                      )}
                    </div>
                  )}

                  {esActual && horesEstimades?.[clau]?.hora_sortida && (
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>
                      Sortida estimada: {horesEstimades[clau].hora_sortida}
                    </p>
                  )}

                  {!esDone && (() => {
                    const lat = punt.lat || punt.dades_json?.lat
                    const lng = punt.lng || punt.dades_json?.lng
                    const adreca = punt.adreca || punt.nom
                    return lat && lng ? (
                      <button
                        onClick={() => openNavigationApp(lat, lng, adreca, 'auto')}
                        style={S.btnGuiar}
                      >
                        🧭 Guiar fins aquí
                      </button>
                    ) : null
                  })()}

                  {esActual && (
                    <div style={S.accionsDomicili}>
                      <button
                        style={{ ...S.btnAccio, backgroundColor: '#34C759', color: '#fff' }}
                        onClick={() => marcarEstat(clauEstat, 'realitzat')}
                        disabled={guardant}
                      >
                        ✅ Fet
                      </button>
                      <button
                        style={{ ...S.btnAccio, backgroundColor: '#FF9500', color: '#fff' }}
                        onClick={() => setModal({ tipus: 'postposar', domicili: punt })}
                        disabled={guardant}
                      >
                        ⏭ Postposar
                      </button>
                      <button
                        style={{ ...S.btnAccio, backgroundColor: '#aeaeb2', color: '#fff' }}
                        onClick={() => marcarEstat(clauEstat, 'saltat')}
                        disabled={guardant}
                      >
                        ❌ Saltar
                      </button>
                      <button
                        style={{ ...S.btnAccio, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}
                        onClick={() => setModal({ tipus: 'revisitar', domicili: punt })}
                        disabled={guardant}
                      >
                        🔄 Revisitar
                      </button>
                    </div>
                  )}

                  {estatVal === 'postposat' && (
                    <p style={{ fontSize: 11, color: '#FF9500', margin: '4px 0 0' }}>⏭ Postposat</p>
                  )}
                </div>
              )}

              {punt.tipus === 'arribada' && (
                <div style={{ ...S.puntCard, borderLeftColor: '#007AFF', backgroundColor: '#F0F7FF' }}>
                  <div style={S.puntTop}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{punt.subtipus === 'hospital' ? '🏥' : '🏡'}</span>
                    <span style={{ ...S.puntCodi, color: '#007AFF' }}>
                      {punt.subtipus === 'hospital' ? 'Tornada a hospital' : 'Tornada a casa'}
                    </span>
                    {horesEstimades?.[clau]?.hora_arribada && (
                      <span style={S.puntHora}>{horesEstimades[clau].hora_arribada}</span>
                    )}
                  </div>
                  {punt.adreca && <p style={S.puntAdreca}>{punt.adreca}</p>}
                  {punt.lat && punt.lng && (
                    <button onClick={() => openNavigationApp(punt.lat, punt.lng, punt.adreca || '', 'auto')} style={S.btnGuiar}>
                      🧭 Guiar fins aquí
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {!domiciliActual && (
          <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
            <p style={{ fontSize: 22, margin: 0 }}>🏁</p>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>
              Tots els domicilis visitats
            </p>
            <button style={{ ...S.btnPrimariBlock, marginTop: 12 }} onClick={finalitzarRuta}>
              Finalitzar ruta
            </button>
          </div>
        )}
      </div>

      {/* Barra inferior: temps estimat i desviació */}
      <div style={S.barraInferior}>
        <div style={S.barraInferiorRow}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#999' }}>Previst</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{horaFiPrevista || '—'}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#999' }}>Desviació</div>
            <div style={{
              fontSize: 16, fontWeight: 700,
              color: desviacio > 15 ? '#FF3B30' : desviacio > 5 ? '#FF9500' : '#34C759',
            }}>
              {desviacio === 0 ? '—' : desviacio > 0 ? `+${desviacio}min` : `${desviacio}min`}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#999' }}>Ara s'estima</div>
            <div style={{
              fontSize: 16, fontWeight: 600,
              color: desviacio > 5 ? '#FF9500' : '#34C759',
            }}>
              {horaFiActual || '—'}
            </div>
          </div>
        </div>
        {(() => {
          const total = sequencia.filter(p => p.tipus === 'domicili' || p.tipus === 'hospital').length
          const fets  = Object.values(estat?.domicilis_estat ?? {})
            .filter(e => e.estat === 'realitzat' || e.estat === 'saltat').length
          const pct = total > 0 ? Math.round((fets / total) * 100) : 0
          return (
            <div style={{ marginTop: 6 }}>
              <div style={{ height: 4, background: '#F0F0F0', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#34C759', borderRadius: 2, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 10, color: '#999', textAlign: 'center', marginTop: 3 }}>
                {fets} de {total} visites completades
              </div>
            </div>
          )
        })()}
      </div>

      {/* Modal Postposar */}
      {modal?.tipus === 'postposar' && (
        <ModalPostposar
          domicili={modal.domicili}
          ruta={{ ...ruta, sequencia, estat }}
          config={config}
          onConfirm={handlePostposar}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal Revisitar */}
      {modal?.tipus === 'revisitar' && (
        <ModalRevisitar
          domicili={modal.domicili}
          onConfirm={minuts => handleRevisitar(modal.domicili, minuts)}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal Recalcular post-fet */}
      {modal?.tipus === 'recalcular' && (
        <ModalRecalcular
          recalculant={recalculant}
          onRecalcular={handleRecalcularDesPosicio}
          onContinuar={() => setModal(null)}
        />
      )}
    </div>
  )
}

// ── Estils ────────────────────────────────────────────────────────────────────

const S = {
  page:   { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f9fafb' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px 10px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  headerTitol: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  headerSub:   { margin: 0, fontSize: 11, color: '#34C759', marginTop: 1 },
  btnBack: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#007AFF', padding: '0 4px', flexShrink: 0 },
  btnFinalitzar: { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },

  // ── Switch mostrar visitats ──────────────────────────────────────────────────
  switchWrap:  { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'pointer', flexShrink: 0 },
  switchTrack: { position: 'relative', width: 34, height: 20, borderRadius: 10, transition: 'background 0.2s' },
  switchThumb: { position: 'absolute', top: 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left 0.2s' },

  // ── Resum final ──────────────────────────────────────────────────────────────
  resumBloc:       { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16, marginBottom: 12 },
  resumStats:      { display: 'flex', gap: 16, marginBottom: 16 },
  resumStat:       { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 },
  resumStatVal:    { fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1 },
  resumStatLabel:  { fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 },
  resumSeccioTitol:{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 6px' },
  resumTemps:      { flex: 1, minWidth: 70, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', textAlign: 'center' },
  resumTempsLabel: { display: 'block', fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' },
  resumTempsVal:   { display: 'block', fontSize: 16, fontWeight: 700, color: '#111827', marginTop: 2 },
  resumDetallFila: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6' },

  // ── Cards de punt ────────────────────────────────────────────────────────────
  puntCard:  { backgroundColor: '#fff', borderRadius: 10, padding: '10px 12px', marginBottom: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderLeft: '4px solid #c7c7cc' },
  puntTop:   { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  puntIcona: { fontSize: 15, flexShrink: 0 },
  puntCodi:  { fontSize: 14, fontWeight: 700, flex: 1 },
  puntHora:  { fontSize: 12, color: '#9ca3af', flexShrink: 0 },
  puntAdreca:{ margin: '2px 0 0 23px', fontSize: 12, color: '#6b7280' },
  visitaBadge: { fontSize: 10, fontWeight: 700, backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: 999, padding: '1px 5px' },

  // ── Leg entre domicilis ──────────────────────────────────────────────────────
  legRow:  { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px' },
  legLine: { flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  legText: { fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' },

  // ── Badges ───────────────────────────────────────────────────────────────────
  badgeIngres: { fontSize: 10, fontWeight: 700, color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 7px', display: 'inline-block' },
  badgeAlta:   { fontSize: 10, fontWeight: 700, color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 7px', display: 'inline-block' },

  // ── Botons d'acció ───────────────────────────────────────────────────────────
  accionsDomicili: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 },
  btnAccio: { flex: 1, minWidth: 70, border: 'none', borderRadius: 8, padding: '8px 4px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnGuiar: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, background: '#F0F7FF', border: '1px solid #007AFF', color: '#007AFF', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginTop: 8 },
  barraInferior:    { position: 'fixed', bottom: 60, left: 0, right: 0, background: 'white', borderTop: '1px solid #E0E0E0', padding: '10px 16px', zIndex: 100, boxShadow: '0 -2px 10px rgba(0,0,0,0.08)' },
  barraInferiorRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 400, margin: '0 auto' },

  // ── Modals ────────────────────────────────────────────────────────────────────
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'flex-end' },
  modalBox:     { backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '20px 20px 36px', width: '100%', maxHeight: '90dvh', overflowY: 'auto', position: 'relative' },
  modalClose:   { position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', padding: 4 },
  modalTitol:   { margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#111827' },
  modalSub:     { margin: '0 0 16px', fontSize: 13, color: '#6b7280' },

  // ── Modal Postposar: grid d'opcions ──────────────────────────────────────────
  opcionesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 },
  opcioCard:    { padding: 14, border: '2px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', textAlign: 'center', transition: 'border-color 0.15s' },
  opcioIcon:    { fontSize: 28, marginBottom: 6 },
  opcioLabel:   { fontSize: 13, fontWeight: 600, color: '#111827' },
  opcioSub:     { fontSize: 11, color: '#9ca3af', marginTop: 4 },
  opcioSelect:  { marginTop: 6, width: '100%', fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid #e5e7eb' },

  // ── Botons genèrics ──────────────────────────────────────────────────────────
  btnPrimariBlock: { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 10, padding: '13px', width: '100%', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'block', boxSizing: 'border-box', textAlign: 'center' },
  btnSecBlock:     { backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, padding: '12px', width: '100%', fontSize: 15, cursor: 'pointer', marginTop: 8, display: 'block', boxSizing: 'border-box', textAlign: 'center' },
}
